import { useCallback, useState } from "react";
// @ts-expect-error pure js
import useAuthenticatedAxios from "./use-authenticated-axios";

interface CalculateSplitParams {
  compteur_type: string;
  periode_debut: string;
  periode_fin: string;
  montant_total: number;
  manual_readings?: Record<number, number>;
}

interface TenantPeriod {
  tenant_name: string;
  amount: number;
}

interface Split {
  property_id: number;
  property_name: string;
  amount: number;
  percentage: number;
  tenant_periods?: TenantPeriod[];
}

interface TenantChange {
  property_id: number;
  property_name: string;
  old_tenant: string;
  new_tenant: string;
  change_date: string;
}

interface MissingReading {
  property_id: number;
  property_name: string;
  date: string;
  type: "start" | "end";
}

export interface SplitResult {
  status: "complete" | "incomplete";
  splits: Split[];
  missing_readings: MissingReading[];
  tenant_changes: TenantChange[];
}

interface ReadingToCreate {
  id_bien: number;
  type: string;
  date_releve: string;
  valeur: number;
}

interface UseMeterSplitReturn {
  calculateSplit: (params: CalculateSplitParams) => Promise<SplitResult>;
  createReadingsBatch: (readings: ReadingToCreate[]) => Promise<unknown>;
  isCalculating: boolean;
  error: string | null;
}

export default function useMeterSplit(
  folderId: number | undefined,
): UseMeterSplitReturn {
  const axios = useAuthenticatedAxios();
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const calculateSplit = useCallback(
    async (params: CalculateSplitParams): Promise<SplitResult> => {
      if (!folderId) {
        throw new Error("Folder ID is required");
      }

      setIsCalculating(true);
      setError(null);

      try {
        const response = await axios.post(
          `/property-folders/${folderId}/meter-split-calculation`,
          params,
        );
        return response.data;
      } catch (err) {
        const errorMessage =
          (
            err as {
              response?: { data?: { message?: string } };
              message?: string;
            }
          ).response?.data?.message ||
          (err as Error).message ||
          "Erreur de calcul";
        setError(errorMessage);
        throw err;
      } finally {
        setIsCalculating(false);
      }
    },
    [folderId, axios],
  );

  const createReadingsBatch = useCallback(
    async (readings: ReadingToCreate[]): Promise<unknown> => {
      if (!folderId) {
        throw new Error("Folder ID is required");
      }

      try {
        const response = await axios.post(
          `/property-folders/${folderId}/compteurs/batch`,
          { readings },
        );
        return response.data;
      } catch (err) {
        const errorMessage =
          (
            err as {
              response?: { data?: { message?: string } };
              message?: string;
            }
          ).response?.data?.message ||
          (err as Error).message ||
          "Erreur lors de la création des relevés";
        setError(errorMessage);
        throw err;
      }
    },
    [folderId, axios],
  );

  return {
    calculateSplit,
    createReadingsBatch,
    isCalculating,
    error,
  };
}
